import { supabase } from '@/lib/supabase';
import { documentNumberService } from '@/app/services/documentNumberService';

/** Per-contact slice from `get_contact_party_gl_balances` (1100 AR, 2000 AP, worker net on 2010/1180). */
export type ContactPartyGlBalancesSlice = {
  glArReceivable: number;
  glApPayable: number;
  glWorkerPayable: number;
};

function isContactCodeUniqueViolation(err: { code?: string; message?: string } | null | undefined): boolean {
  if (!err || err.code !== '23505') return false;
  const m = String(err.message || '');
  return m.includes('idx_contacts_company_code_unique') || (m.includes('code') && m.includes('duplicate'));
}

async function syncOpeningGlForContact(contactId: string | undefined | null) {
  if (!contactId) return;
  try {
    const { openingBalanceJournalService } = await import('./openingBalanceJournalService');
    await openingBalanceJournalService.syncFromContactRow(contactId);
  } catch (e) {
    console.error('[contactService] Opening balance GL sync failed:', e);
  }
}

export interface Contact {
  id?: string;
  company_id: string;
  branch_id?: string;
  type: 'customer' | 'supplier' | 'both' | 'worker' | 'money_exchange';
  name: string;
  code?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  cnic?: string;
  ntn?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  opening_balance?: number;
  current_balance?: number;
  credit_limit?: number;
  payment_terms?: number;
  tax_number?: string;
  notes?: string;
  /** Worker contacts: default Rs amount for Studio "Worker Cost" when assigning (optional). */
  worker_default_rate?: number;
  worker_role?: string;
  is_active?: boolean;
  is_system_generated?: boolean;
  system_type?: string;
  is_default?: boolean;
  created_by?: string;
}

export const contactService = {
  // Get all contacts
  async getAllContacts(companyId: string, type?: 'customer' | 'supplier' | 'worker' | 'both') {
    let query = supabase
      .from('contacts')
      .select('*')
      .eq('company_id', companyId)
      .order('name');

    // Note: is_active column may not exist - removed filter to avoid errors
    // If needed, filter in application code after fetching

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  /** Alias for `getAllContacts` — Party Ledger and pickers use this name. */
  async getContacts(companyId: string, type?: 'customer' | 'supplier' | 'worker' | 'both') {
    return this.getAllContacts(companyId, type);
  },

  /**
   * Get receivables/payables per contact in one RPC (avoids loading all sales + purchases).
   * On failure, `error` is set and `map` is empty — callers must not substitute client-side “merged” math as if it were canonical.
   */
  async getContactBalancesSummary(
    companyId: string,
    branchId?: string | null,
    asOfDate?: string | null
  ): Promise<{
    map: Map<string, { receivables: number; payables: number }>;
    signedReceivablesTotal: number;
    signedPayablesTotal: number;
    error: string | null;
    reasonTag: 'ok' | 'rpc_missing' | 'rpc_empty';
  }> {
    // GL-only cutover: derive balances from party GL RPC (1100/2000/2010).
    const partyMap = await this.getContactPartyGlBalancesMap(companyId, branchId, asOfDate);
    if (!partyMap) {
      return {
        map: new Map(),
        signedReceivablesTotal: 0,
        signedPayablesTotal: 0,
        error: 'get_contact_party_gl_balances failed',
        reasonTag: 'rpc_missing',
      };
    }
    const map = new Map<string, { receivables: number; payables: number }>();
    let signedReceivablesTotal = 0;
    let signedPayablesTotal = 0;
    partyMap.forEach((row, contactId) => {
      const ar = Number(row.glArReceivable) || 0;
      const ap = (Number(row.glApPayable) || 0) + (Number(row.glWorkerPayable) || 0);
      signedReceivablesTotal += ar;
      signedPayablesTotal += ap;
      map.set(String(contactId), {
        receivables: Math.max(0, ar),
        payables: Math.max(0, ap),
      });
    });
    return {
      map,
      signedReceivablesTotal,
      signedPayablesTotal,
      error: null,
      reasonTag: map.size > 0 ? 'ok' : 'rpc_empty',
    };
  },

  /**
   * Per-contact GL slice (AR/AP/worker) from journal attribution — for UI comparison to operational
   * (`get_contact_balances_summary`), not for replacing row operational amounts.
   * Returns null if RPC missing / error (e.g. migration not applied).
   */
  async getContactPartyGlBalancesMap(
    companyId: string,
    branchId?: string | null,
    asOfDate?: string | null
  ): Promise<Map<string, ContactPartyGlBalancesSlice> | null> {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const safeBranchId =
      branchId && branchId !== 'all' && typeof branchId === 'string' && uuidRegex.test(branchId.trim())
        ? branchId.trim()
        : null;
    const asOf = asOfDate?.slice(0, 10) || null;
    const { data, error } = await supabase.rpc('get_contact_party_gl_balances', {
      p_company_id: companyId,
      p_branch_id: safeBranchId,
      p_as_of_date: asOf,
    });
    if (error) {
      if (import.meta.env?.DEV) {
        console.warn('[CONTACT SERVICE] get_contact_party_gl_balances RPC error:', error.message);
      }
      return null;
    }
    const map = new Map<string, ContactPartyGlBalancesSlice>();
    (data ?? []).forEach(
      (row: {
        contact_id: string;
        gl_ar_receivable?: number | string | null;
        gl_ap_payable?: number | string | null;
        gl_worker_payable?: number | string | null;
      }) => {
        if (!row?.contact_id) return;
        map.set(String(row.contact_id), {
          glArReceivable: Number(row.gl_ar_receivable ?? 0) || 0,
          glApPayable: Number(row.gl_ap_payable ?? 0) || 0,
          glWorkerPayable: Number(row.gl_worker_payable ?? 0) || 0,
        });
      }
    );
    return map;
  },

  // Get single contact
  async getContact(id: string) {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Create contact
  async createContact(contact: Partial<Contact> & Record<string, unknown>) {
    // Strip undefined so Supabase doesn't get invalid/missing column values
    const clean = (obj: Record<string, unknown>): Record<string, unknown> => {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined && v !== null) out[k] = v;
      }
      return out;
    };
    let payload = clean(contact as Record<string, unknown>);
    // Ensure required fields exist
    if (!payload.company_id || !payload.type || !payload.name) {
      throw new Error('company_id, type, and name are required');
    }

    // Auto-generate contact code from global sequence. Skip for walk-in (code already set).
    const companyIdStr = String(payload.company_id);
    if (!payload.code && !payload.is_system_generated) {
      const codeType =
        (payload.type === 'customer' || payload.type === 'both') ? 'CUS' :
        payload.type === 'supplier' ? 'SUP' :
        payload.type === 'worker' ? 'WRK' : null;
      if (codeType) {
        try {
          const code = await documentNumberService.getNextDocumentNumberGlobal(companyIdStr, codeType);
          payload = { ...payload, code };
        } catch (e) {
          console.warn(`[CONTACT SERVICE] get_next_document_number_global for ${codeType} failed:`, e);
        }
      }
    }

    // Insert with retry on code unique constraint violation (max 3 attempts)
    const MAX_CODE_RETRIES = 3;
    let lastInsertError: any = null;
    for (let attempt = 0; attempt < MAX_CODE_RETRIES; attempt++) {
      const { data, error } = await supabase
        .from('contacts')
        .insert(payload)
        .select()
        .single();

      if (!error) {
        await syncOpeningGlForContact((data as { id?: string })?.id);
        const newId = (data as { id?: string; type?: string })?.id;
        if (newId) {
          supabase
            .rpc('ensure_party_subledgers_for_contact', { p_contact_id: newId })
            .then(({ error: rpcErr }) => {
              if (rpcErr) {
                import('./partySubledgerAccountService')
                  .then(({ ensurePartySubledgersForContact }) =>
                    ensurePartySubledgersForContact(companyIdStr, newId, String((data as { type?: string }).type || payload.type))
                  )
                  .catch(() => {});
              }
            })
            .catch(() => {});
        }
        return data;
      }

      // If code unique violation, get next code and retry
      if (isContactCodeUniqueViolation(error) && payload.code) {
        lastInsertError = error;
        const codeType =
          (payload.type === 'customer' || payload.type === 'both') ? 'CUS' :
          payload.type === 'supplier' ? 'SUP' :
          payload.type === 'worker' ? 'WRK' : null;
        if (codeType) {
          try {
            const newCode = await documentNumberService.getNextDocumentNumberGlobal(companyIdStr, codeType);
            payload = { ...payload, code: newCode };
            continue;
          } catch { /* fall through to error handling */ }
        }
      }

      lastInsertError = error;
      break;
    }

    const error = lastInsertError;
    if (!error) {
      throw new Error('Contact could not be created (no error returned from database)');
    }
    const isBadRequest = error.code === 'PGRST204' || error.code === 'PGRST116' || (error as any).status === 400;
    if (!isBadRequest) throw error;

    const errorMessage = (error.message || '').toLowerCase();
    // Columns that may not exist until migration is run
    const optionalColumns = [
      'country', 'contact_person', 'group_id', 'business_name', 'code',
      'payable_account_id', 'supplier_opening_balance', 'worker_role', 'worker_default_rate',
      'branch_id', 'current_balance', 'is_default', 'is_system_generated', 'system_type'
    ];
    const toStrip = optionalColumns.filter(
      (col) => errorMessage.includes(col.replace(/_/g, ' ')) || errorMessage.includes("'" + col + "'")
    );
    if (toStrip.length > 0) {
      const reduced = { ...payload };
      toStrip.forEach((col) => delete reduced[col]);
      const { data: retryData, error: retryError } = await supabase
        .from('contacts')
        .insert(reduced)
        .select()
        .single();
      if (!retryError) {
        await syncOpeningGlForContact((retryData as { id?: string })?.id);
        const newId = (retryData as { id?: string; type?: string })?.id;
        if (newId) {
          import('./partySubledgerAccountService')
            .then(({ ensurePartySubledgersForContact }) =>
              ensurePartySubledgersForContact(String(payload.company_id), newId, String((retryData as { type?: string }).type || payload.type))
            )
            .catch(() => {});
        }
        return retryData;
      }
    }

    // Retry with only base schema columns (no optional/extended columns)
    const baseColumns = [
      'company_id', 'type', 'name', 'code', 'email', 'phone', 'mobile', 'cnic', 'ntn',
      'address', 'city', 'state', 'country', 'postal_code', 'tax_number',
      'opening_balance', 'supplier_opening_balance', 'credit_limit', 'payment_terms', 'notes', 'is_active', 'created_by', 'branch_id', 'is_default'
    ];
    const minimal: Record<string, unknown> = {};
    baseColumns.forEach((k) => {
      if (payload[k] !== undefined && payload[k] !== null) minimal[k] = payload[k];
    });
    const { data: minimalData, error: minimalError } = await supabase
      .from('contacts')
      .insert(minimal)
      .select()
      .single();
    if (!minimalError) {
      await syncOpeningGlForContact((minimalData as { id?: string })?.id);
      const newId = (minimalData as { id?: string; type?: string })?.id;
      if (newId) {
        import('./partySubledgerAccountService')
          .then(({ ensurePartySubledgersForContact }) =>
            ensurePartySubledgersForContact(String(payload.company_id), newId, String((minimalData as { type?: string }).type || payload.type))
          )
          .catch(() => {});
      }
      return minimalData;
    }

    console.error('[CONTACT SERVICE] createContact failed:', error.message, minimalError?.message);
    throw error;
  },

  // Update contact
  // PROTECTION: Cannot change name or type for system-generated contacts
  async updateContact(id: string, updates: Partial<Contact>) {
    // Check if contact is system-generated
    const { data: contact, error: fetchError } = await supabase
      .from('contacts')
      .select('is_default, is_system_generated, system_type')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    // Block name and type changes for default / system-generated Walk-in Customer
    if (contact?.is_default === true || (contact?.is_system_generated && contact?.system_type === 'walking_customer')) {
      if (updates.name !== undefined && updates.name !== 'Walking Customer') {
        throw new Error('Cannot rename default Walking Customer. This is a system-generated contact.');
      }
      if (updates.type !== undefined && updates.type !== 'customer') {
        throw new Error('Cannot change type of default Walking Customer. This is a system-generated contact.');
      }
    }

    const { data, error } = await supabase
      .from('contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    await syncOpeningGlForContact((data as { id?: string })?.id);
    return data;
  },

  // Delete contact (soft delete)
  // PROTECTION: Cannot delete system-generated contacts
  async deleteContact(id: string) {
    // Check if contact is default/system-generated
    const { data: contact, error: fetchError } = await supabase
      .from('contacts')
      .select('is_default, is_system_generated, system_type')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    // Block deletion of default or system-generated Walk-in Customer
    if (contact?.is_default || (contact?.is_system_generated && contact?.system_type === 'walking_customer')) {
      throw new Error('Default Walk-in Customer cannot be deleted. This is a system-generated contact.');
    }

    const { error } = await supabase
      .from('contacts')
      .update({ is_active: false })
      .eq('id', id);

    if (error) throw error;
  },

  // ============================================================================
  // SYSTEM-GENERATED CONTACTS
  // ============================================================================

  /**
   * Create default "Walk-in Customer" for the company (one per company only; no branch).
   * Code = CUS-0000 (reserved). Used when no walk-in exists yet.
   * DB enforces single walk-in per company via unique_walkin_per_company_strict.
   */
  async createDefaultWalkingCustomer(companyId: string): Promise<Contact> {
    const existing = await this.getDefaultCustomer(companyId);
    if (existing) return existing;

    const walkingCustomer: Partial<Contact> = {
      company_id: companyId,
      type: 'customer',
      name: 'Walk-in Customer',
      code: 'CUS-0000',
      is_active: true,
      is_system_generated: true,
      system_type: 'walking_customer',
      is_default: true,
      opening_balance: 0,
      credit_limit: 0,
      payment_terms: 0,
    };

    try {
      const created = await this.createContact(walkingCustomer);
      console.log('[CONTACT SERVICE] ✅ Walk-in customer created (one per company):', created.id);
      return created as Contact;
    } catch (error: any) {
      console.error('[CONTACT SERVICE] ❌ Failed to create walking customer:', error);
      throw new Error(`Failed to create default walking customer: ${error.message}`);
    }
  },

  /**
   * Get the mandatory default (Walk-in) customer for the company.
   * Exactly one per company (company_id only; no branch_id or created_by).
   * Used for new sale auto-selection and for resolving walk-in customer_id on save.
   */
  async getDefaultCustomer(companyId: string): Promise<Contact | null> {
    // Strict: walk-in by company_id only (system_type = 'walking_customer')
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('company_id', companyId)
      .eq('system_type', 'walking_customer')
      .in('type', ['customer', 'both'])
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[CONTACT SERVICE] Error fetching default customer:', error);
      return null;
    }
    if (data) return data as Contact;
    // Fallback for legacy: is_default before strict enforcement migration
    const { data: legacy } = await supabase
      .from('contacts')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_default', true)
      .in('type', ['customer', 'both'])
      .limit(1)
      .maybeSingle();
    return (legacy as Contact) || null;
  },

  /**
   * Get walk-in customer for the company (company_id only; no branch/user).
   * Used in SaleForm for auto-select and for resolving customer_id when saving "walk-in".
   * Lookup: WHERE company_id = ? AND system_type = 'walking_customer' (or is_default) LIMIT 1.
   */
  async getWalkingCustomer(companyId: string): Promise<Contact | null> {
    return this.getDefaultCustomer(companyId);
  },

  /**
   * Ensure default Walk-in Customer exists for the company (one per company only).
   */
  async ensureDefaultWalkingCustomerForCompany(companyId: string): Promise<void> {
    try {
      await this.createDefaultWalkingCustomer(companyId);
    } catch (_) {
      // Already exists or other error – ignore so load can continue
    }
  },

  /**
   * AP sub-ledger leaf accounts sometimes lack linked_contact_id; match account display name to a single supplier/both contact.
   */
  async resolveSupplierContactIdFromSubledgerAccountName(companyId: string, accountName: string): Promise<string | null> {
    const raw = String(accountName || '').trim();
    if (!raw) return null;
    const candidates = [
      ...new Set(
        [
          raw,
          raw.replace(/\s*-\s*AP\s*$/i, '').trim(),
          raw.replace(/\s*\(AP\)\s*$/i, '').trim(),
          raw.replace(/\s*\/\s*Supplier\s*$/i, '').trim(),
        ].filter(Boolean)
      ),
    ];
    for (const name of candidates) {
      const { data, error } = await supabase
        .from('contacts')
        .select('id')
        .eq('company_id', companyId)
        .in('type', ['supplier', 'both'])
        .ilike('name', name)
        .limit(2);
      if (error || !data?.length) continue;
      if (data.length === 1) return (data[0] as { id: string }).id;
    }
    return null;
  },

  /** AR sub-ledger leaf without linked_contact_id: match name to a single customer/both contact. */
  async resolveCustomerContactIdFromSubledgerAccountName(companyId: string, accountName: string): Promise<string | null> {
    const raw = String(accountName || '').trim();
    if (!raw) return null;
    const strippedReceivable = raw.replace(/^receivable\s*[—–-]\s*/i, '').trim();
    const candidates = [
      ...new Set(
        [
          raw,
          strippedReceivable,
          raw.replace(/\s*-\s*AR\s*$/i, '').trim(),
          raw.replace(/\s*\(AR\)\s*$/i, '').trim(),
        ].filter(Boolean)
      ),
    ];
    for (const name of candidates) {
      const { data, error } = await supabase
        .from('contacts')
        .select('id')
        .eq('company_id', companyId)
        .in('type', ['customer', 'both'])
        .ilike('name', name)
        .limit(2);
      if (error || !data?.length) continue;
      if (data.length === 1) return (data[0] as { id: string }).id;
    }
    return null;
  },

  /** Assign CUS/SUP/WRK when contacts.code is empty (mobile backfill / manual). */
  async assignContactReferenceNumber(
    contactId: string,
  ): Promise<{ success: boolean; code?: string; error?: string; alreadyAssigned?: boolean }> {
    const { data, error } = await supabase.rpc('assign_contact_reference_number', {
      p_contact_id: contactId,
    });
    if (error) {
      return { success: false, error: error.message || 'Could not assign reference number' };
    }
    const result = data as {
      success?: boolean;
      code?: string;
      error?: string;
      already_assigned?: boolean;
    };
    return {
      success: !!result?.success,
      code: result?.code,
      error: result?.error,
      alreadyAssigned: !!result?.already_assigned,
    };
  },

  /** Assign CUS/SUP/WRK code and mark public-form lead as Approved. */
  async approvePublicLead(contactId: string): Promise<{ success: boolean; code?: string; error?: string }> {
    const { data, error } = await supabase.rpc('approve_public_contact_lead', {
      p_contact_id: contactId,
    });
    if (error) {
      return { success: false, error: error.message || 'Approval failed' };
    }
    const result = data as { success?: boolean; code?: string; error?: string };
    return {
      success: !!result?.success,
      code: result?.code,
      error: result?.error,
    };
  },

  // Search contacts
  async searchContacts(companyId: string, query: string) {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('company_id', companyId)
      .or(`name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`)
      .limit(20);

    // Note: is_active column may not exist - removed filter to avoid errors

    if (error) throw error;
    return data;
  },
};
